import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { InventoryManufacturedMaterialViewModel } from "../../../viewmodels/inventory/manufacturedMaterial/InventoryManufacturedMaterialViewModel";

import { connect } from "react-redux";
import * as actions from "../../../store/actions/index";
import {
  formatCurrency,
  formatQuantity,
  withRouter,
} from "../../../shared/utility";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import SelectMenu from "../../../components/controls/SelectMenu";
import OperationalDataTableSkeleton from "../../../components/operational/OperationalDataTableSkeleton";
import OperationalAnalyticsStrip from "../../../components/operational/OperationalAnalyticsStrip";
import OperationalTableToolbar from "../../../components/operational/OperationalTableToolbar";
import InventoryManufacturedSubTable from "../../../components/inventory/InventoryManufacturedSubTable";
import Button from "../../../components/ui/Button";
import {
  fetchLocationZonesForDropdown,
  fetchLocationsForDropdown,
} from "../../../shared/locationDropdowns";
import { makePostApiCall } from "../../../shared/makeApiCall";

const ALL_VALUE = 0;
const FIELD_SELECT_CLASS = "w-[16rem] min-w-[16rem] max-w-[16rem] shrink-0";
const FIELD_PANEL_CLASS = "inventory-filter-select-panel";

function FilterField({ label, children }) {
  return (
    <div className="flex w-[16rem] min-w-[16rem] max-w-[16rem] shrink-0 flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
        {label}
      </span>
      {children}
    </div>
  );
}

function InventoryManufacturedMaterialScreen(props) {
  const { logout } = props;
  const {
    getInStockRawMaterial,
    loading,
    products,
    exportNetsuiteInventoryAdjustmentCsv,
    subRowDetails,
    zoneDetails,
    lotDetails,
    cartonDetails,
    fetchSubRowDetails,
    fetchZones,
    fetchLots,
    fetchCartons,
    loadingRows,
    loadingZones,
    loadingLots,
    loadingCartons,
    locationGroupKey,
    zoneGroupKey,
    lotGroupKey,
    cartonKey,
  } = InventoryManufacturedMaterialViewModel(logout);

  const currentPageRef = useRef(1);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [expandedRows, setExpandedRows] = useState(null);
  const [locationId, setLocationId] = useState(ALL_VALUE);
  const [zoneId, setZoneId] = useState(ALL_VALUE);
  const [locationOptions, setLocationOptions] = useState([
    { value: ALL_VALUE, label: "All locations" },
  ]);
  const [zoneOptions, setZoneOptions] = useState([
    { value: ALL_VALUE, label: "All zones" },
  ]);
  const [productTypeId, setProductTypeId] = useState(ALL_VALUE);
  const [productTypeOptions, setProductTypeOptions] = useState([
    { value: ALL_VALUE, label: "All product types" },
  ]);
  const [appliedSearch, setAppliedSearch] = useState(null);

  const searchRef = useRef();

  const selectedLocationId = locationId > 0 ? locationId : null;
  const selectedZoneId = zoneId > 0 ? zoneId : null;
  const selectedProductTypeId = productTypeId > 0 ? productTypeId : null;
  const hasActiveFilters = Boolean(
    selectedLocationId || selectedZoneId || selectedProductTypeId,
  );

  const getSearchValue = () => searchRef.current?.value?.trim() || null;

  const fetchPage = (
    page,
    nextLocationId = selectedLocationId,
    nextZoneId = selectedZoneId,
    nextProductTypeId = selectedProductTypeId,
  ) => {
    const search = getSearchValue();
    setAppliedSearch(search);
    getInStockRawMaterial(
      search,
      page,
      nextLocationId,
      nextZoneId,
      nextProductTypeId,
    );
  };

  useEffect(() => {
    getInStockRawMaterial(null, currentPageRef.current, null, null, null);
    fetchLocationsForDropdown().then((result) => {
      if (result.success) {
        setLocationOptions([
          { value: ALL_VALUE, label: "All locations" },
          ...(result.data ?? []),
        ]);
      }
    });
    makePostApiCall("productTypesForDropdown").then((result) => {
      if (result.success) {
        setProductTypeOptions([
          { value: ALL_VALUE, label: "All product types" },
          ...(result.data ?? []),
        ]);
      } else if (result.errorStatus === 401) {
        logout();
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedLocationId) {
      setZoneOptions([{ value: ALL_VALUE, label: "All zones" }]);
      return;
    }

    let cancelled = false;
    fetchLocationZonesForDropdown({ value: selectedLocationId }).then(
      (result) => {
        if (cancelled || !result.success) {
          return;
        }
        setZoneOptions([
          { value: ALL_VALUE, label: "All zones" },
          ...(result.data ?? []),
        ]);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [selectedLocationId]);

  const handleSearch = () => {
    currentPageRef.current = 1;
    setFirst(0);
    setExpandedRows(null);
    fetchPage(1);
  };

  const handlePagination = (event) => {
    const page = event.page + 1;
    currentPageRef.current = page;
    setFirst(event.first);
    setRows(event.rows);
    setExpandedRows(null);
    fetchPage(page);
  };

  const handleLocationChange = (event) => {
    const nextLocation = event.target.value?.value ?? ALL_VALUE;
    const nextLocationId = nextLocation > 0 ? nextLocation : null;
    currentPageRef.current = 1;
    setFirst(0);
    setLocationId(nextLocation > 0 ? nextLocation : ALL_VALUE);
    setZoneId(ALL_VALUE);
    setExpandedRows(null);
    fetchPage(1, nextLocationId, null, selectedProductTypeId);
  };

  const handleZoneChange = (event) => {
    const nextZone = event.target.value?.value ?? ALL_VALUE;
    const nextZoneId = nextZone > 0 ? nextZone : null;
    currentPageRef.current = 1;
    setFirst(0);
    setZoneId(nextZone > 0 ? nextZone : ALL_VALUE);
    setExpandedRows(null);
    fetchPage(1, selectedLocationId, nextZoneId, selectedProductTypeId);
  };

  const handleProductTypeChange = (event) => {
    const nextType = event.target.value?.value ?? ALL_VALUE;
    const nextProductTypeId = nextType > 0 ? nextType : null;
    currentPageRef.current = 1;
    setFirst(0);
    setProductTypeId(nextType > 0 ? nextType : ALL_VALUE);
    setExpandedRows(null);
    fetchPage(1, selectedLocationId, selectedZoneId, nextProductTypeId);
  };

  const handleClearFilters = () => {
    currentPageRef.current = 1;
    setFirst(0);
    setLocationId(ALL_VALUE);
    setZoneId(ALL_VALUE);
    setProductTypeId(ALL_VALUE);
    setExpandedRows(null);
    fetchPage(1, null, null, null);
  };

  const onRowExpand = async (event) => {
    const rowData = event.data;
    const key = locationGroupKey(
      rowData.product_id,
      appliedSearch,
      selectedLocationId,
      selectedZoneId,
      selectedProductTypeId,
    );
    if (!subRowDetails[key]) {
      await fetchSubRowDetails(
        rowData,
        appliedSearch,
        selectedLocationId,
        selectedZoneId,
        selectedProductTypeId,
      );
    }
  };

  const rowExpansionTemplate = (data) => {
    const search = appliedSearch;
    const key = locationGroupKey(
      data.product_id,
      search,
      selectedLocationId,
      selectedZoneId,
      selectedProductTypeId,
    );

    return (
      <InventoryManufacturedSubTable
        groups={subRowDetails[key]}
        loading={loadingRows[key]}
        zonesByKey={zoneDetails}
        loadingZones={loadingZones}
        zoneKeyFor={(location) =>
          zoneGroupKey(
            location.product_id,
            location.location_id,
            search,
            selectedZoneId,
            selectedProductTypeId,
          )
        }
        onExpandLocation={(location) => {
          const zoneCacheKey = zoneGroupKey(
            location.product_id,
            location.location_id,
            search,
            selectedZoneId,
            selectedProductTypeId,
          );
          if (!zoneDetails[zoneCacheKey]) {
            fetchZones(location, search, selectedZoneId, selectedProductTypeId);
          }
        }}
        lotsByKey={lotDetails}
        loadingLots={loadingLots}
        lotKeyFor={(zone) =>
          lotGroupKey(
            zone.product_id,
            zone.location_id,
            zone.zone_id,
            search,
            selectedProductTypeId,
          )
        }
        onExpandZone={(zone) => {
          const lotCacheKey = lotGroupKey(
            zone.product_id,
            zone.location_id,
            zone.zone_id,
            search,
            selectedProductTypeId,
          );
          if (!lotDetails[lotCacheKey]) {
            fetchLots(zone, search, selectedProductTypeId);
          }
        }}
        cartonsByKey={cartonDetails}
        loadingCartons={loadingCartons}
        cartonKeyFor={(lot) =>
          cartonKey(
            lot.product_id,
            lot.location_id,
            lot.zone_id,
            lot.lot_number,
            search,
            selectedProductTypeId,
          )
        }
        onExpandLot={(lot) => {
          const cartonCacheKey = cartonKey(
            lot.product_id,
            lot.location_id,
            lot.zone_id,
            lot.lot_number,
            search,
            selectedProductTypeId,
          );
          if (!cartonDetails[cartonCacheKey]) {
            fetchCartons(lot, search, selectedProductTypeId);
          }
        }}
        unitOfMeasure={data.unit_of_measure}
      />
    );
  };

  const totalRecords = products?.total ?? 0;
  const resultMeta = `${totalRecords.toLocaleString()} product${totalRecords === 1 ? "" : "s"}`;

  const analyticsStats = [
    {
      name: "Total Manufactured In Stock",
      stat: formatCurrency(products.totalStockDollarAmount),
      subtitle: "Across all products",
      icon: "targetWeight",
    },
    {
      name: "Page Total Manufactured In Stock",
      stat: formatCurrency(products.totalPageStockDollarAmount),
      subtitle: "Current page",
      icon: "manufacturedWeight",
    },
  ];

  const emptyMessage = (
    <div className="px-6 py-12 text-center">
      <p className="text-sm text-[#64748B]">
        No manufactured inventory matches your search or filters.
      </p>
    </div>
  );

  return (
    <div className="qatrace-screen qatrace-module-page">
      <div className="space-y-5 px-4 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
                <li>
                  <Link
                    to="/inventory-manufactured-material"
                    className="text-[#16A34A] transition hover:text-[#15803D]"
                  >
                    Inventory
                  </Link>
                </li>
                <li className="text-[#CBD5E1]" aria-hidden="true">
                  /
                </li>
                <li className="text-[#475569]">Manufactured Products</li>
              </ol>
            </nav>
            <h1 className="mt-2 text-xl font-semibold tracking-tight text-[#0F172A]">
              In stock Manufactured Products
            </h1>
          </div>

          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={exportNetsuiteInventoryAdjustmentCsv}
          >
            Export CSV
          </Button>
        </header>

        <OperationalAnalyticsStrip stats={analyticsStats} loading={loading} />

        <div className="overflow-visible rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <OperationalTableToolbar
            searchRow={
              <>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#94A3B8]"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  name="searchManufacturedInventory"
                  id="searchManufacturedInventory"
                  className="block min-h-10 w-full rounded-xl border border-[#E2E8F0] bg-white py-2 pl-10 pr-3 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
                  placeholder="Search by product name, lot, or PLU"
                  ref={searchRef}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
              </>
            }
            footer={
              <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
                <FilterField label="Location">
                  <SelectMenu
                    handleInputChange={handleLocationChange}
                    parentClass={FIELD_SELECT_CLASS}
                    panelClassName={FIELD_PANEL_CLASS}
                    placeholder="All locations"
                    name="location"
                    options={locationOptions}
                    value={locationId}
                  />
                </FilterField>
                <FilterField label="Zone">
                  <SelectMenu
                    handleInputChange={handleZoneChange}
                    parentClass={FIELD_SELECT_CLASS}
                    panelClassName={FIELD_PANEL_CLASS}
                    placeholder="All zones"
                    name="zone"
                    options={zoneOptions}
                    value={zoneId}
                    disabled={!selectedLocationId}
                  />
                </FilterField>
                <FilterField label="Product type">
                  <SelectMenu
                    handleInputChange={handleProductTypeChange}
                    parentClass={FIELD_SELECT_CLASS}
                    panelClassName={FIELD_PANEL_CLASS}
                    placeholder="All product types"
                    name="product_type"
                    options={productTypeOptions}
                    value={productTypeId}
                  />
                </FilterField>
                {hasActiveFilters ? (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="mb-2 text-xs font-semibold text-[#16A34A] transition hover:text-[#15803D]"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            }
            trailing={
              <span className="text-xs font-medium text-[#64748B]">
                {resultMeta}
              </span>
            }
          />

          <div className="operational-embedded-table overflow-hidden">
            {loading ? (
              <OperationalDataTableSkeleton
                columns={[
                  "PLU",
                  "Product Name",
                  "Description",
                  "Case weight",
                  "Quantity in stock by weight",
                  "Quantity in stock/case",
                  "Total Dollar Amount",
                  "",
                ]}
                rows={10}
              />
            ) : (
              <DataTable
                value={products.data ?? []}
                expandedRows={expandedRows}
                onRowToggle={(event) => setExpandedRows(event.data)}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="product_id"
                className="p-datatable-sm text-sm"
                emptyMessage={emptyMessage}
              >
                <Column field="plu" header="PLU" style={{ minWidth: "6rem" }} />
                <Column
                  field="product_name"
                  header="Product Name"
                  style={{ minWidth: "12rem" }}
                  bodyClassName="font-medium text-[#0F172A]"
                />
                <Column
                  field="description_en"
                  header="Description"
                  style={{ minWidth: "12rem" }}
                />
                <Column
                  header="Case weight"
                  alignHeader="right"
                  body={(row) =>
                    formatQuantity(row.packing_size, row.unit_of_measure)
                  }
                  style={{ minWidth: "8rem", textAlign: "right" }}
                  bodyClassName="text-right tabular-nums"
                />
                <Column
                  header="Quantity in stock by weight"
                  alignHeader="right"
                  body={(row) =>
                    formatQuantity(row.stock_in_weight, row.unit_of_measure)
                  }
                  style={{ minWidth: "12rem", textAlign: "right" }}
                  bodyClassName="text-right tabular-nums"
                />
                <Column
                  header="Quantity in stock/case"
                  alignHeader="right"
                  field="total_individual_units"
                  style={{ minWidth: "10rem", textAlign: "right" }}
                  bodyClassName="text-right tabular-nums"
                />
                <Column
                  header="Total Dollar Amount"
                  alignHeader="right"
                  body={(row) => (
                    <span className="tabular-nums font-medium text-[#0F172A]">
                      {formatCurrency(row.itemDollarAmount)}
                    </span>
                  )}
                  style={{ minWidth: "9rem", textAlign: "right" }}
                  bodyClassName="text-right tabular-nums"
                />
                <Column
                  expander={() => true}
                  header=""
                  style={{ width: "5rem" }}
                />
              </DataTable>
            )}
          </div>

          <div className="border-t border-[#E2E8F0] px-4 py-4 sm:px-5">
            <Paginator
              className="operational-embedded-paginator"
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              onPageChange={handlePagination}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(InventoryManufacturedMaterialScreen),
);
